import { getLeCoffreAccessTokenCookieForContext } from "@Front/Utils/lecoffreSessionCookieHelpers";
import {
	getJwtExpiration,
	isCustomerJwtPayload,
	isThirdPartyJwtPayload,
	isUserJwtPayload,
	type AnyJwtPayload,
} from "@Front/Types/JwtPayloads";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";

type RefreshAuthContext = "notary" | "customer" | "thirdparty";
type DecodeJwtPayloadFn = (token: string) => AnyJwtPayload | null;

function resolveAuthContextFromRefreshPayload(payload: AnyJwtPayload): RefreshAuthContext | null {
	if (isThirdPartyJwtPayload(payload)) return "thirdparty";
	if (isCustomerJwtPayload(payload)) return "customer";
	if (isUserJwtPayload(payload)) return "notary";
	return null;
}

function resolveAccessTokenExpiration(payload: AnyJwtPayload, decodeJwtPayload: DecodeJwtPayloadFn): number | null {
	const context = resolveAuthContextFromRefreshPayload(payload);
	if (!context) {
		LoggerService.warn("⚠️ [SessionRefresh] Unsupported refresh token payload", { payload });
		return null;
	}
	const accessToken = getLeCoffreAccessTokenCookieForContext(context);
	if (!accessToken) {
		LoggerService.warn("⚠️ [SessionRefresh] Missing access token for refresh context", { context });
		return null;
	}
	const accessPayload = decodeJwtPayload(accessToken);
	if (!accessPayload) {
		return null;
	}
	const expiration = getJwtExpiration(accessPayload);
	if (typeof expiration !== "number") {
		LoggerService.warn("⚠️ [SessionRefresh] Access token payload has no exp", { context });
		return null;
	}
	return expiration;
}

export function resolveNextRefreshDelayMs(
	payload: AnyJwtPayload,
	options: {
		decodeJwtPayload: DecodeJwtPayloadFn;
		refreshMarginMs: number;
		minDelayMs: number;
		fallbackDelayMs: number;
	},
): number {
	const accessTokenExpiration = resolveAccessTokenExpiration(payload, options.decodeJwtPayload);
	if (typeof accessTokenExpiration === "number") {
		const refreshAtMs = accessTokenExpiration * 1000 - options.refreshMarginMs;
		return Math.max(options.minDelayMs, refreshAtMs - Date.now());
	}
	return options.fallbackDelayMs;
}

export function resolveRefreshContext(payload: AnyJwtPayload): RefreshAuthContext | null {
	return resolveAuthContextFromRefreshPayload(payload);
}
